import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { CellValueChangedEvent } from 'ag-grid-community';
import { AdvancedReportConfig } from '@app/advanced-reports/models/advanced-report-config';
import { GridColumn } from '@entity-framework/entity-grid/state/entity-grid-column';
import { TabExportData } from '@app/advanced-reports/dynamic-reporting/excel-export/advanced-reports-excel-export-orchestration.service';
import { BehaviorSubject, Observable, of, Subject } from 'rxjs';
import {
  catchError,
  concatMap,
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  shareReplay,
  startWith,
  switchMap,
  take,
  tap
} from 'rxjs/operators';
import { getIntQueryParam } from '@utils/route-utils';
import { pensionContributionsGridColumns } from './pension-contributions-grid-columns';
import { pensionPaymentErrorsGridColumns } from './pension-payment-errors-grid-columns';
import { pensionEnrollmentGridColumns } from './pension-enrollment-grid-columns';
import { PensionContributionsDataProvider } from './state/pension-contributions.data-provider';
import { TabConfig } from '@design/buttons/tab-list/tab-config';
import { combineLatest, firstValueFrom } from 'rxjs';
import { ContributionReasonCode, PensionContributionEmployee, PensionSubmissionError } from './state/pension-contribution-employee.model';
import { PensionEnrollmentRecord } from './state/pension-enrollment-record.model';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ClosedPeriodOperationType } from '../models/closed-period-operation-type.enum';
import { OnscreenMessagingService } from '@pattern-library/onscreen-messaging/onscreen-messaging.service';

type TabView = 'Enrollment data' | 'Contributions' | 'Submission errors' | 'Payment errors';
type PensionRowData = PensionEnrollmentRecord[] | PensionContributionEmployee[] | PensionSubmissionError[];

const tabTids: Record<TabView, string> = {
  'Enrollment data': 'pension-enrollment-data-tab',
  Contributions: 'pension-contributions-tab',
  'Submission errors': 'pension-submission-errors-tab',
  'Payment errors': 'pension-payment-errors-tab'
};

const defaultTabPriority: TabView[] = ['Payment errors', 'Submission errors', 'Contributions'];

@UntilDestroy()
@Component({
  templateUrl: './pension-contributions-advanced-view.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [PensionContributionsDataProvider],
  styleUrls: ['./pension-contributions-advanced-view.component.scss'],
  standalone: false
})
export class PensionContributionsAdvancedViewComponent {
  private readonly selectedViewSubject = new BehaviorSubject<TabView | null>(null);
  private readonly closedPeriodId: number | null;
  readonly inFlightSubject = new BehaviorSubject<boolean>(true);

  private readonly reasonEditSubject = new Subject<{ employmentId: string; reasonCode: number }>();
  private readonly pendingEdits = new Map<string, { reasonCode: number; oldValue: unknown; event: CellValueChangedEvent }>();
  private readonly messaging = inject(OnscreenMessagingService);
  private isRevertingCellValue = false;

  protected readonly enrollmentData$: Observable<PensionEnrollmentRecord[]>;
  protected readonly contributionsData$: Observable<PensionContributionEmployee[]>;
  protected readonly submissionErrorsData$: Observable<PensionSubmissionError[]>;
  protected readonly paymentErrorsData$: Observable<PensionSubmissionError[]>;
  protected readonly reasonCodes$: Observable<ContributionReasonCode[]>;
  private readonly availableViews$: Observable<TabView[]>;
  private readonly activeView$: Observable<TabView>;
  readonly rowData$: Observable<PensionRowData>;
  readonly config$: Observable<AdvancedReportConfig>;
  readonly tabs$: Observable<TabConfig[]>;

  constructor(
    private readonly route: ActivatedRoute,
    private readonly dataProvider: PensionContributionsDataProvider
  ) {
    this.closedPeriodId = getIntQueryParam(this.route, 'closedPeriodId');

    this.enrollmentData$ = this.initializeEnrollmentData();
    this.contributionsData$ = this.initializeContributionsData();
    this.submissionErrorsData$ = this.initializeSubmissionErrorsData();
    this.paymentErrorsData$ = this.initializePaymentErrorsData();
    this.reasonCodes$ = this.initializeReasonCodes();
    this.availableViews$ = this.initializeAvailableViews();
    this.activeView$ = this.initializeActiveView();
    this.rowData$ = this.initializeRowData();
    this.config$ = this.initializeConfig();
    this.tabs$ = this.initializeTabs();
    this.initializeReasonOverrideSave();
  }

  getTabExportData = async (tabLabel: string): Promise<Partial<TabExportData> | null> => {
    let rowData$: Observable<PensionRowData>;
    let gridColumns: GridColumn[] = [];

    switch (tabLabel) {
      case 'Enrollment data':
        rowData$ = this.enrollmentData$;
        gridColumns = pensionEnrollmentGridColumns();
        break;
      case 'Contributions': {
        rowData$ = this.contributionsData$;
        const [contributions, reasonCodes] = await Promise.all([
          firstValueFrom(this.contributionsData$),
          firstValueFrom(this.reasonCodes$)
        ]);
        gridColumns = this.getContributionsGridColumns(contributions ?? [], reasonCodes ?? []);
        break;
      }
      case 'Submission errors':
        rowData$ = this.submissionErrorsData$;
        gridColumns = pensionPaymentErrorsGridColumns();
        break;
      case 'Payment errors':
        rowData$ = this.paymentErrorsData$;
        gridColumns = pensionPaymentErrorsGridColumns();
        break;
      default:
        rowData$ = this.enrollmentData$;
        gridColumns = pensionEnrollmentGridColumns();
    }

    const rowData = await firstValueFrom(rowData$);

    return {
      tabLabel,
      rowData,
      gridColumns
    };
  };

  onCellValueChanged(event: CellValueChangedEvent): void {
    if (this.isRevertingCellValue) {
      return;
    }

    if (event.colDef?.field !== 'contributionReductionReason' || !event.data?.employmentId) {
      return;
    }

    const employmentId = event.data.employmentId as string;
    const reasonCode = Number(event.newValue);

    if (isNaN(reasonCode) || event.newValue == null) {
      return;
    }

    this.pendingEdits.set(employmentId, { reasonCode, oldValue: event.oldValue, event });
    this.reasonEditSubject.next({ employmentId, reasonCode });
  }

  private initializeReasonOverrideSave(): void {
    if (!this.closedPeriodId) {
      return;
    }

    const closedPeriodId = this.closedPeriodId;

    this.reasonEditSubject
      .pipe(
        untilDestroyed(this),
        debounceTime(300),
        filter(() => this.pendingEdits.size > 0),
        concatMap(() => {
          const edits = new Map(this.pendingEdits);
          this.pendingEdits.clear();

          const overrides = Array.from(edits.entries()).map(([employmentId, edit]) => ({
            employmentId,
            reasonCode: edit.reasonCode
          }));

          return this.dataProvider.saveContributionReasonOverrides$(closedPeriodId, overrides).pipe(
            tap(() => {
              edits.forEach((edit) => {
                const rowNode = edit.event.node;
                if (rowNode?.data) {
                  rowNode.data.hasReasonOverride = true;
                }
              });
            }),
            catchError(() => {
              this.isRevertingCellValue = true;
              try {
                edits.forEach((edit) => {
                  const rowNode = edit.event.node;
                  if (rowNode) {
                    rowNode.setDataValue('contributionReductionReason', edit.oldValue);
                  }
                });
              } finally {
                this.isRevertingCellValue = false;
              }
              this.messaging.warn('Failed to save reason code. Please try again.');
              return of(undefined);
            })
          );
        })
      )
      .subscribe();
  }

  private initializeEnrollmentData(): Observable<PensionEnrollmentRecord[]> {
    if (!this.closedPeriodId) {
      return of([]);
    }

    return this.dataProvider.getSubmissionEnrollments$(this.closedPeriodId).pipe(
      untilDestroyed(this),
      take(1),
      map((records) => this.sortErrorsFirstThenByName(records, (r) => r.hasErrors)),
      shareReplay(1)
    );
  }

  private initializeContributionsData(): Observable<PensionContributionEmployee[]> {
    if (!this.closedPeriodId) {
      return of([]);
    }

    return this.dataProvider.getSubmissionContributions$(this.closedPeriodId).pipe(
      untilDestroyed(this),
      take(1),
      map((contributions) => this.sortErrorsFirstThenByName(contributions, (c) => c.errors?.length > 0)),
      shareReplay(1)
    );
  }

  private initializeSubmissionErrorsData(): Observable<PensionSubmissionError[]> {
    if (!this.closedPeriodId) {
      return of([]);
    }

    return this.dataProvider
      .getClosedPeriodErrors$(this.closedPeriodId, [ClosedPeriodOperationType.Enrollment, ClosedPeriodOperationType.Contribution])
      .pipe(untilDestroyed(this), take(1), shareReplay(1));
  }

  private initializePaymentErrorsData(): Observable<PensionSubmissionError[]> {
    if (!this.closedPeriodId) {
      return of([]);
    }

    return this.dataProvider
      .getClosedPeriodErrors$(this.closedPeriodId, [ClosedPeriodOperationType.PaymentApproval])
      .pipe(untilDestroyed(this), take(1), shareReplay(1));
  }

  private initializeReasonCodes(): Observable<ContributionReasonCode[]> {
    return this.contributionsData$.pipe(
      switchMap((contributions) => {
        const needsReasonCodes = contributions.some((c) => c.requiresReasonCode);
        if (!needsReasonCodes) return of([] as ContributionReasonCode[]);
        return this.dataProvider.getContributionReasonCodes$();
      }),
      take(1),
      untilDestroyed(this),
      shareReplay(1)
    );
  }

  private initializeAvailableViews(): Observable<TabView[]> {
    return combineLatest([this.enrollmentData$, this.contributionsData$, this.submissionErrorsData$, this.paymentErrorsData$]).pipe(
      untilDestroyed(this),
      map(([enrollments, contributions, submissionErrors, paymentErrors]) => {
        const recordCountsByView: [TabView, number][] = [
          ['Enrollment data', enrollments.length],
          ['Contributions', contributions.length],
          ['Submission errors', submissionErrors.length],
          ['Payment errors', paymentErrors.length]
        ];
        return recordCountsByView.filter(([, count]) => count > 0).map(([view]) => view);
      }),
      shareReplay(1)
    );
  }

  private initializeActiveView(): Observable<TabView> {
    return combineLatest([this.availableViews$, this.selectedViewSubject]).pipe(
      untilDestroyed(this),
      map(([availableViews, selectedView]) => this.resolveActiveView(availableViews, selectedView)),
      distinctUntilChanged(),
      shareReplay(1)
    );
  }

  private initializeRowData(): Observable<PensionRowData> {
    return this.activeView$.pipe(
      untilDestroyed(this),
      tap(() => this.inFlightSubject.next(true)),
      switchMap((view) => {
        switch (view) {
          case 'Enrollment data':
            return this.enrollmentData$;
          case 'Contributions':
            return this.contributionsData$;
          case 'Submission errors':
            return this.submissionErrorsData$;
          case 'Payment errors':
            return this.paymentErrorsData$;
          default:
            return this.enrollmentData$;
        }
      }),
      tap(() => this.inFlightSubject.next(false))
    );
  }

  private initializeConfig(): Observable<AdvancedReportConfig> {
    return this.activeView$.pipe(
      untilDestroyed(this),
      switchMap((view) => {
        const contributions$ = view === 'Contributions' ? this.contributionsData$ : of([] as PensionContributionEmployee[]);
        const codes$ =
          view === 'Contributions' ? this.reasonCodes$.pipe(startWith([] as ContributionReasonCode[])) : of([] as ContributionReasonCode[]);
        return combineLatest([contributions$, codes$]).pipe(map(([contributions, reasonCodes]) => ({ view, contributions, reasonCodes })));
      }),
      map(({ view, contributions, reasonCodes }) => {
        const columns = this.getColumnsForView(view, contributions, reasonCodes);
        const groupedColumn = columns.find((c) => c.colDefOverrides?.rowGroup);

        return {
          gridId: `pension-${view.toLowerCase().replace(/\s+/g, '-')}-v4`,
          reportTitle: 'Pensions connect / Contributions',
          reportType: 'standard' as const,
          gridColumns$: of(columns),
          ...(groupedColumn && {
            autoGroupColumnDef: {
              headerName: groupedColumn.headerLabel,
              width: 300,
              cellRendererParams: { suppressCount: true }
            }
          })
        };
      })
    );
  }

  private initializeTabs(): Observable<TabConfig[]> {
    return combineLatest([this.availableViews$, this.activeView$]).pipe(
      untilDestroyed(this),
      map(([availableViews, activeView]) =>
        availableViews.map((view) => ({
          label: view,
          tid: tabTids[view],
          active: view === activeView,
          navigate: () => this.selectedViewSubject.next(view)
        }))
      )
    );
  }

  private resolveActiveView(availableViews: TabView[], selectedView: TabView | null): TabView {
    if (selectedView && availableViews.includes(selectedView)) {
      return selectedView;
    }
    return defaultTabPriority.find((view) => availableViews.includes(view)) ?? availableViews[0] ?? 'Enrollment data';
  }

  private getColumnsForView(
    view: TabView,
    contributions: PensionContributionEmployee[] = [],
    reasonCodes: ContributionReasonCode[] = []
  ): GridColumn[] {
    switch (view) {
      case 'Enrollment data':
        return pensionEnrollmentGridColumns();
      case 'Contributions':
        return this.getContributionsGridColumns(contributions ?? [], reasonCodes);
      case 'Submission errors':
        return pensionPaymentErrorsGridColumns();
      case 'Payment errors':
        return pensionPaymentErrorsGridColumns();
      default:
        return pensionEnrollmentGridColumns();
    }
  }

  private getContributionsGridColumns(contributions: PensionContributionEmployee[], reasonCodes: ContributionReasonCode[]): GridColumn[] {
    const distinctGroups = new Set(contributions.map((c) => c.employerRefPaymentSource ?? '').filter((g) => g !== '')).size;
    const showEmployerRefPaymentSourceColumn = contributions.some((c) => c.hasEmployerRefPaymentSourceFromApi === true);
    const enableRowGroup = distinctGroups >= 2 && showEmployerRefPaymentSourceColumn;
    return pensionContributionsGridColumns(reasonCodes, enableRowGroup, showEmployerRefPaymentSourceColumn);
  }

  private sortErrorsFirstThenByName<T extends { firstName: string | null; surname: string | null }>(
    items: T[],
    hasErrors: (item: T) => boolean
  ): T[] {
    return items.slice().sort((a, b) => {
      if (hasErrors(a) && !hasErrors(b)) return -1;
      if (!hasErrors(a) && hasErrors(b)) return 1;
      const surnameCompare = (a.surname ?? '').localeCompare(b.surname ?? '');
      return surnameCompare !== 0 ? surnameCompare : (a.firstName ?? '').localeCompare(b.firstName ?? '');
    });
  }
}
